import { Alert, Button, Card, Divider, Empty, Modal, notification, Space, Tag, Tooltip, Typography } from "antd";
import { useOutletContext, useParams } from "react-router";
import { useState, useMemo } from "react";
import { CheckCircle2, ExternalLink, Globe, Lock, Loader2, Plus, Trash2, AlertCircle, Undo2, ArrowLeft } from "lucide-react";
import { useRecoilValue } from "recoil";
import { currentProjectState } from "../../recoil/project";
import DomainWizard, { DomainWizardResult } from "../../components/settings/DomainWizard";
import { IComponentDetails, IEnvDetails } from "../../types";
import { getEnvironmentService } from "../../backend";
import { trackQuickDeploy } from "../../backend/tracking/deployment";
import { MicroserviceConfigurationItem } from "@microtica/ms-kube-sdk";
import React from "react";

const { Text, Title } = Typography;

// Helper function to create pipeline-style status tags
const createStatusTag = (
    icon: React.ReactElement,
    text: string,
    color: string,
    size: "small" | "large" = "small"
) => {
    const fontSize = size === "large" ? "10px" : "9px";
    const padding = size === "large" ? "3px 8px" : "2px 6px";
    const iconSize = size === "large" ? "11px" : "10px";
    const textColor = color === "#059669" ? "#047857" : color; // Darker green for success states

    return (
        <div style={{
            fontSize: fontSize,
            fontWeight: 700,
            textTransform: "uppercase",
            letterSpacing: "0.04em",
            color: textColor,
            flexShrink: 0,
            padding: padding,
            borderRadius: "3px",
            backgroundColor: `${color}10`,
            border: `1px solid ${color}20`,
            display: "inline-flex",
            alignItems: "center",
            gap: "4px"
        }}>
            <span style={{
                color: color,
                display: "inline-flex",
                alignItems: "center"
            }}>
                {React.cloneElement(icon, { size: iconSize })}
            </span>
            {text}
        </div>
    );
};

interface DomainItem {
    id: string;
    domain: string;
    type: 'static' | 'dynamic';
    status: 'configured' | 'not-configured';
    certArn?: string;
    cname: string;
    propPrefix?: string;
    // For static domains
    propertyKey?: string;
    // For dynamic domains
    dynamicPropKey?: string;
    // Marked for deletion
    markedForDeletion?: boolean;
}

const ResourceSettingsCustomDomain = () => {
    const { envId, projectId } = useParams<{ envId: string; projectId: string }>();
    const { env, component: componentFromContext } = useOutletContext<{ env: IEnvDetails, component: IComponentDetails }>();
    const currentProject = useRecoilValue(currentProjectState);

    const [isWizardOpen, setIsWizardOpen] = useState(false);
    const [wizardContext, setWizardContext] = useState<{
        type: 'static' | 'dynamic';
        domainItem?: DomainItem;
        dynamicPropKey?: string;
    } | null>(null);
    const [deploying, setDeploying] = useState(false);
    const [pendingChanges, setPendingChanges] = useState<Map<string, DomainWizardResult>>(new Map());
    const [removedDynamicDomains, setRemovedDynamicDomains] = useState<Set<string>>(new Set());
    const [wizardFooter, setWizardFooter] = useState<React.ReactNode>(null);
    const [component, setComponent] = useState<IComponentDetails>(componentFromContext);

    // Update component when context changes
    React.useEffect(() => {
        setComponent(componentFromContext);
    }, [componentFromContext]);

    // Check for dynamic-domain schema properties
    const schemaProperties = component.component.schema?.properties?.inputs?.properties || {};
    const dynamicDomainProps = Object.entries(schemaProperties).filter(
        ([_, prop]) => prop.category === "domain" && prop.subcategory === "dynamic-domain"
    );
    
    // Check for static domain schema properties (category "domain" but not "dynamic-domain")
    const staticDomainProps = Object.entries(schemaProperties).filter(
        ([_, prop]) => prop.category === "domain" && prop.subcategory !== "dynamic-domain"
    );

    // Check for legacy domain properties (backward compatibility)
    // Look for properties ending with "DomainName" that have corresponding "CertificateArn" or "CertificateARN"
    const hasLegacyDomainSupport = useMemo(() => {
        const propertyKeys = Object.keys(schemaProperties);
        return propertyKeys.some(key => {
            if (key.endsWith("DomainName")) {
                const prefix = key.replace("DomainName", "");
                // Check for corresponding CertificateArn or CertificateARN
                const hasCertArn = propertyKeys.some(certKey => 
                    certKey === `${prefix}CertificateArn` || certKey === `${prefix}CertificateARN`
                );
                return hasCertArn;
            }
            return false;
        });
    }, [schemaProperties]);

    // Get the shared CNAME
    const sharedCNAME = component.domains?.[0]?.CNAME || component.domain.CNAME;

    // Parse existing dynamic domains from configuration
    const parseExistingDynamicDomains = (propKey: string): DomainItem[] => {
        const config = component.configurations?.find(c => c.key === propKey);
        if (!config?.value || config.value.trim() === "") return [];
        return config.value
            .split(",")
            .map((item, index) => {
                const [domain, certArn] = item.split("=").map(s => s.trim());
                return {
                    id: `dynamic-${propKey}-${index}`,
                    domain: domain || "",
                    type: 'dynamic' as const,
                    status: 'configured' as const,
                    certArn: certArn || undefined,
                    cname: sharedCNAME!,
                    dynamicPropKey: propKey
                };
            })
            .filter(item => item.domain !== "");
    };

    // Build unified domain list
    const allDomains = useMemo(() => {
        const domains: DomainItem[] = [];
        const seenDomains = new Set<string>();

        // Helper to normalize domain name for comparison (lowercase, trim)
        const normalizeDomain = (domain: string) => domain.toLowerCase().trim();

        // Helper to check and add domain if not duplicate
        const addDomainIfUnique = (domainItem: DomainItem) => {
            // For domains with names, check for duplicates
            if (domainItem.domain) {
                const normalizedDomain = normalizeDomain(domainItem.domain);
                if (!seenDomains.has(normalizedDomain)) {
                    seenDomains.add(normalizedDomain);
                    domains.push(domainItem);
                }
            } else {
                // For unconfigured domains without names, use propPrefix as unique identifier
                const uniqueKey = domainItem.propPrefix || domainItem.id;
                if (!seenDomains.has(uniqueKey)) {
                    seenDomains.add(uniqueKey);
                    domains.push(domainItem);
                }
            }
        };

        // Add static domains from component.domains array
        if (component.domains && component.domains.length > 0) {
            component.domains.forEach((domain, index) => {
                const isConfigured = !domain.displayAssignDomainBtn && domain.accessUrl;
                // Extract domain name from accessUrl if configured, otherwise try to get from configurations
                let domainName = '';
                if (isConfigured && domain.accessUrl) {
                    domainName = domain.accessUrl.replace('https://', '').replace('http://', '');
                } else if (domain.propPrefix) {
                    // For unconfigured domains, try to get domain name from configuration
                    const domainConfig = component.configurations?.find(c => c.key === `${domain.propPrefix}DomainName`);
                    if (domainConfig?.value) {
                        domainName = domainConfig.value;
                    }
                }
                
                // Add domain if it's eligible for configuration
                if (domain.eligibleForDomainConfiguration) {
                    addDomainIfUnique({
                        id: `static-${index}`,
                        domain: domainName, // May be empty for unconfigured domains
                        type: 'static',
                        status: isConfigured ? 'configured' : 'not-configured',
                        cname: domain.CNAME!,
                        propPrefix: domain.propPrefix
                    });
                }
            });
        }
        
        // Also add the base domain if it's eligible and not already in the list
        // Check if base domain CNAME is already in the domains list
        const baseDomainAlreadyAdded = component.domains?.some(d => d.CNAME === component.domain?.CNAME);
        if (component.domain && component.domain.eligibleForDomainConfiguration && !baseDomainAlreadyAdded) {
            const isBaseConfigured = !component.domain.displayAssignDomainBtn && component.domain.accessUrl;
            let baseDomainName = '';
            if (isBaseConfigured && component.domain.accessUrl) {
                baseDomainName = component.domain.accessUrl.replace('https://', '').replace('http://', '');
            } else {
                // Try to get from configuration
                const baseDomainConfig = component.configurations?.find(c => c.key === 'DomainName');
                if (baseDomainConfig?.value) {
                    baseDomainName = baseDomainConfig.value;
                }
            }
            
            addDomainIfUnique({
                id: 'static-base',
                domain: baseDomainName,
                type: 'static',
                status: isBaseConfigured ? 'configured' : 'not-configured',
                cname: component.domain.CNAME!,
                propPrefix: undefined
            });
        }

        // Add dynamic domains (including those marked for deletion)
        dynamicDomainProps.forEach(([propKey]) => {
            const existingDomains = parseExistingDynamicDomains(propKey);
            existingDomains.forEach(d => {
                const domainItem = {
                    ...d,
                    markedForDeletion: removedDynamicDomains.has(d.domain)
                };
                addDomainIfUnique(domainItem);
            });
        });

        // Add pending domains
        pendingChanges.forEach((result, key) => {
            if (key.startsWith('dynamic-')) {
                const propKey = key.replace('dynamic-', '').split('-')[0];
                addDomainIfUnique({
                    id: key,
                    domain: result.domain,
                    type: 'dynamic',
                    status: 'configured',
                    certArn: result.certArn,
                    cname: sharedCNAME!,
                    dynamicPropKey: propKey
                });
            }
        });

        return domains;
    }, [component.domains, dynamicDomainProps, pendingChanges, removedDynamicDomains, sharedCNAME]);

    const staticDomains = allDomains.filter(d => d.type === 'static');
    // Sort dynamic domains to show pending ones first
    const dynamicDomains = useMemo(() => {
        const domains = allDomains.filter(d => d.type === 'dynamic');
        return domains.sort((a, b) => {
            const aIsPending = pendingChanges.has(a.id);
            const bIsPending = pendingChanges.has(b.id);
            if (aIsPending && !bIsPending) return -1;
            if (!aIsPending && bIsPending) return 1;
            return 0;
        });
    }, [allDomains, pendingChanges]);

    const hasUnsavedChanges = pendingChanges.size > 0 || removedDynamicDomains.size > 0;

    const awsAccount = {
        id: env.awsAccountId!,
        region: sharedCNAME?.endsWith("cloudfront.net") ? "us-east-1" : env.region!
    };

    const handleOpenStaticWizard = (domainItem: DomainItem) => {
        setWizardContext({ type: 'static', domainItem });
        setIsWizardOpen(true);
    };

    const handleOpenDynamicWizard = (dynamicPropKey: string) => {
        setWizardContext({ type: 'dynamic', dynamicPropKey });
        setIsWizardOpen(true);
    };

    const handleWizardComplete = async (result: DomainWizardResult) => {
        // Normalize domain name for comparison (case-insensitive)
        const normalizeDomain = (domain: string) => domain.toLowerCase().trim();
        const normalizedNewDomain = normalizeDomain(result.domain);

        // Check if domain already exists
        const existingDomain = allDomains.find(d => 
            d.domain && normalizeDomain(d.domain) === normalizedNewDomain
        );

        if (wizardContext?.type === 'static' && wizardContext.domainItem) {
            // For static domains, deploy immediately (updates cert ARN if domain exists)
            await deployStaticDomain(wizardContext.domainItem, result);
        } else if (wizardContext?.type === 'dynamic' && wizardContext.dynamicPropKey) {
            // For dynamic domains, update existing domain's cert ARN or add new one
            if (existingDomain && existingDomain.type === 'dynamic' && existingDomain.dynamicPropKey === wizardContext.dynamicPropKey) {
                // Update existing domain's certificate ARN
                // Find and update the pending change or create a new one
                const existingPendingKey = Array.from(pendingChanges.keys()).find(key => 
                    key.startsWith(`dynamic-${wizardContext.dynamicPropKey}`) && 
                    normalizeDomain(pendingChanges.get(key)!.domain) === normalizedNewDomain
                );
                
                if (existingPendingKey) {
                    // Update existing pending change
                    setPendingChanges(prev => {
                        const updated = new Map(prev);
                        updated.set(existingPendingKey, result);
                        return updated;
                    });
                } else {
                    // Create new pending change to update the cert ARN
                    const key = `dynamic-${wizardContext.dynamicPropKey}-${Date.now()}`;
                    setPendingChanges(prev => new Map(prev).set(key, result));
                }
                notification.success({
                    message: "Certificate Updated",
                    description: "Click 'Deploy Changes' to apply the updated certificate configuration."
                });
            } else {
                // Add new domain
                const key = `dynamic-${wizardContext.dynamicPropKey}-${Date.now()}`;
                setPendingChanges(prev => new Map(prev).set(key, result));
                notification.success({
                    message: "Domain Added",
                    description: "Click 'Deploy Changes' to apply the new domain configuration."
                });
            }
        }
        setIsWizardOpen(false);
        setWizardContext(null);
    };

    const handleRemoveDynamicDomain = (domain: string) => {
        setRemovedDynamicDomains(prev => {
            const newSet = new Set(prev);
            if (newSet.has(domain)) {
                newSet.delete(domain); // Undo deletion
            } else {
                newSet.add(domain); // Mark for deletion
            }
            return newSet;
        });
    };

    const deployStaticDomain = async (domainItem: DomainItem, result: DomainWizardResult) => {
        setDeploying(true);
        try {
            const { data: { resources } } = await getEnvironmentService().getStageDetails(envId!, currentProject!.id);
            const resource = resources.find(r => r.name === component.name);
            const resourceInputParams = Object.keys(resource?.component.schema.properties.inputs.properties || {});
            const prefix = domainItem.propPrefix || "";

            const resourceConfigs = resourceInputParams.reduce((configs, key) => {
                if (key === `${prefix}DomainName`) {
                    configs.push({ key, value: result.domain, reference: false, sensitive: false });
                } else if (key === `${prefix}CertificateArn` || key === `${prefix}CertificateARN`) {
                    configs.push({ key, value: result.certArn, reference: false, sensitive: false });
                } else {
                    const existingConfig = resource?.configurations.find(c => c.key === key);
                    if (existingConfig) configs.push(existingConfig);
                }
                return configs;
            }, [] as MicroserviceConfigurationItem[]);

            await getEnvironmentService().updateResource(envId!, component.name, currentProject!.id, {
                componentVersion: component.version,
                configurations: resourceConfigs
            });

            await getEnvironmentService().deployStage(envId!, currentProject!.id, {
                partial: true,
                resourceVersionOverrides: { [component.name]: component.version }
            });

            notification.success({
                message: "Deployment in Progress",
                description: (
                    <>
                        It could take a few moments for the changes to take effect.
                        <div><a href={`/projects/${currentProject!.id}/pipelines`}>View in Pipelines</a></div>
                    </>
                ),
                icon: <Loader2 size={16} color="var(--primary-color)" className="animate-spin" />
            });

            trackQuickDeploy(envId!);
        } catch (error: any) {
            notification.error({
                message: "Error deploying",
                description: error?.response?.data?.message || "An unexpected error occurred."
            });
        } finally {
            setDeploying(false);
        }
    };

    const deployDynamicChanges = async () => {
        if (!dynamicDomainProps.length) return;

        setDeploying(true);
        try {
            const currentConfigs = component.configurations || [];
            const updatedConfigs = currentConfigs
                .filter(c => !dynamicDomainProps.some(([propKey]) => propKey === c.key))
                .map(c => ({ key: c.key, value: c.value, sensitive: c.sensitive || false, reference: c.reference || false }));

            // Build new dynamic domain values
            dynamicDomainProps.forEach(([propKey]) => {
                const existingDomains = parseExistingDynamicDomains(propKey)
                    .filter(d => !removedDynamicDomains.has(d.domain));

                const pendingDomainsForProp: DomainWizardResult[] = [];
                pendingChanges.forEach((result, key) => {
                    if (key.startsWith(`dynamic-${propKey}`)) {
                        pendingDomainsForProp.push(result);
                    }
                });

                // Normalize domain names for duplicate checking (case-insensitive)
                const normalizeDomain = (domain: string) => domain.toLowerCase().trim();
                const domainMap = new Map<string, string>(); // Map normalized domain -> domain=certArn string

                // Add existing domains first (checking for duplicates)
                existingDomains.forEach(d => {
                    const normalized = normalizeDomain(d.domain);
                    if (!domainMap.has(normalized)) {
                        domainMap.set(normalized, `${d.domain}=${d.certArn || ''}`);
                    }
                });

                // Add/update pending domains (pending changes override existing cert ARNs)
                pendingDomainsForProp.forEach(d => {
                    const normalized = normalizeDomain(d.domain);
                    domainMap.set(normalized, `${d.domain}=${d.certArn}`);
                });

                const uniqueDomains = Array.from(domainMap.values());

                const allDomainsForProp = uniqueDomains.join(',');

                if (allDomainsForProp) {
                    updatedConfigs.push({ key: propKey, value: allDomainsForProp, sensitive: false, reference: false });
                }
            });

            await getEnvironmentService().updateResource(envId!, component.name, projectId!, {
                componentVersion: component.version,
                configurations: updatedConfigs
            });

            await getEnvironmentService().deployStage(envId!, currentProject!.id, {
                partial: true,
                resourceVersionOverrides: { [component.name]: component.version }
            });

            // Update component with the new configurations we just saved
            setComponent(prev => ({
                ...prev,
                configurations: updatedConfigs.map(c => ({
                    key: c.key,
                    value: c.value,
                    sensitive: c.sensitive,
                    reference: c.reference
                }))
            }));

            notification.success({
                message: "Deployment in Progress",
                description: (
                    <>
                        It could take a few moments for the changes to take effect.
                        <div><a href={`/projects/${currentProject!.id}/pipelines`}>View in Pipelines</a></div>
                    </>
                ),
                icon: <Loader2 size={16} color="var(--primary-color)" className="animate-spin" />
            });

            setPendingChanges(new Map());
            setRemovedDynamicDomains(new Set());
            trackQuickDeploy(envId!);
        } catch (error: any) {
            notification.error({
                message: "Error deploying",
                description: error?.response?.data?.message || "An unexpected error occurred."
            });
        } finally {
            setDeploying(false);
        }
    };

    const DomainRow = ({ item, onConfigure, onRemove, isLast }: { item: DomainItem; onConfigure?: () => void; onRemove?: () => void; isLast?: boolean }) => {
        const isConfigured = item.status === 'configured';
        const isPending = pendingChanges.has(item.id);
        const isMarkedForDeletion = item.markedForDeletion;

        return (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '16px 0',
                    borderBottom: isLast ? 'none' : '1px solid var(--border-color, #f0f0f0)',
                    opacity: isMarkedForDeletion ? 0.6 : 1
                }}
            >
                <Space size={16} align="center">
                    <div
                        style={{
                            width: 40,
                            height: 40,
                            borderRadius: 10,
                            backgroundColor: isMarkedForDeletion 
                                ? 'rgba(245, 34, 45, 0.1)' 
                                : isConfigured 
                                    ? 'rgba(82, 196, 26, 0.1)' 
                                    : 'rgba(0, 0, 0, 0.04)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <Globe size={20} color={isMarkedForDeletion ? '#f5222d' : isConfigured ? '#52c41a' : '#8c8c8c'} />
                    </div>
                    <div>
                        <Space size={8} align="center" wrap>
                            <Text 
                                strong 
                                style={{ 
                                    fontSize: 14,
                                    textDecoration: isMarkedForDeletion ? 'line-through' : 'none',
                                    color: isMarkedForDeletion ? '#8c8c8c' : undefined
                                }}
                            >
                                {item.domain || 'Not configured'}
                            </Text>
                            {item.propPrefix && (
                                createStatusTag(<Globe size={10} />, item.propPrefix, "#3b82f6")
                            )}
                            {isMarkedForDeletion && (
                                createStatusTag(<AlertCircle size={10} />, "Marked for deletion", "#f5222d")
                            )}
                        </Space>
                        <div style={{ marginTop: 2 }}>
                            {item.domain ? (
                                isMarkedForDeletion ? (
                                    <Text 
                                        type="secondary" 
                                        style={{ 
                                            fontSize: 12,
                                            textDecoration: 'line-through'
                                        }}
                                    >
                                        https://{item.domain}
                                    </Text>
                                ) : (
                                    <a
                                        href={`https://${item.domain}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                    >
                                        https://{item.domain}
                                        <ExternalLink size={10} />
                                    </a>
                                )
                            ) : (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Click "Configure" to set up this domain
                                </Text>
                            )}
                        </div>
                    </div>
                </Space>

                <Space size={8}>
                    {isConfigured && item.certArn && !isPending && !isMarkedForDeletion && (
                        <Tooltip title={item.certArn}>
                            {createStatusTag(<Lock size={10} />, "SSL", "#059669")}
                        </Tooltip>
                    )}
                    {isPending ? (
                        createStatusTag(<AlertCircle size={10} />, "Pending", "#f59e0b")
                    ) : isConfigured && !isMarkedForDeletion && (
                        createStatusTag(<CheckCircle2 size={10} />, "Active", "#059669")
                    )}
                    {onConfigure && !isMarkedForDeletion && (
                        <Button 
                            size="small" 
                            type={isConfigured ? "default" : "primary"} 
                            ghost={!isConfigured}
                            onClick={onConfigure}
                        >
                            {isConfigured ? "Update" : "Configure"}
                        </Button>
                    )}
                    {item.type === 'dynamic' && onRemove && (
                        <Button
                            size="small"
                            type={isMarkedForDeletion ? "default" : "text"}
                            danger={!isMarkedForDeletion}
                            icon={isMarkedForDeletion ? <Undo2 size={14} /> : <Trash2 size={14} />}
                            onClick={onRemove}
                        >
                            {isMarkedForDeletion ? "Undo" : undefined}
                        </Button>
                    )}
                </Space>
            </div>
        );
    };

    // Check if domain configuration is supported
    const isDomainSupported = component.domain.eligibleForDomainConfiguration || dynamicDomainProps.length > 0;

    if (!isDomainSupported) {
        return (
            <Card>
                <Empty
                    image={<Globe size={48} style={{ color: '#d9d9d9' }} />}
                    description={
                        <Space direction="vertical" size={4}>
                            <Text strong>Domain Configuration Not Available</Text>
                            <Text type="secondary">
                                This component doesn't support custom domain configuration.
                            </Text>
                        </Space>
                    }
                />
            </Card>
        );
    }

    return (
        <Space direction="vertical" size={24} className="full-width">
            {/* Header Card */}
            <Card>
                <Card.Meta
                    title="Base domain"
                    description="Your site is always available at this domain."
                />
                {sharedCNAME && (
                    <>
                        <Divider />
                        <div style={{ padding: '4px 0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                                <a
                                    href={`https://${sharedCNAME}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ 
                                        fontSize: 15, 
                                        fontWeight: 500,
                                        color: '#1890ff',
                                        textDecoration: 'none',
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        gap: 6
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.textDecoration = 'underline'}
                                    onMouseLeave={(e) => e.currentTarget.style.textDecoration = 'none'}
                                >
                                    {sharedCNAME}
                                    <ExternalLink size={14} style={{ flexShrink: 0 }} />
                                </a>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                <Text type="secondary" style={{ fontSize: 13 }}>Status</Text>
                                {createStatusTag(<CheckCircle2 size={10} />, "Published", "#059669")}
                            </div>
                        </div>
                    </>
                )}
            </Card>

            {/* Static Domains Section */}
            {(staticDomainProps.length > 0 || hasLegacyDomainSupport) && (
                <Card>
                    <Card.Meta
                        title="Primary Domains"
                        description="Configure primary domains for your application. Each domain requires its own SSL certificate."
                    />
                    <Divider />
                    {staticDomains.length > 0 ? (
                        <div style={{ margin: '-8px 0' }}>
                            {staticDomains.map((domain, index) => (
                                <DomainRow
                                    key={domain.id}
                                    item={domain}
                                    onConfigure={() => handleOpenStaticWizard(domain)}
                                    isLast={index === staticDomains.length - 1}
                                />
                            ))}
                        </div>
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <Text type="secondary">
                                    No primary domains configured. Domains will appear here once configured.
                                </Text>
                            }
                        />
                    )}
                </Card>
            )}

            {/* Dynamic Domains Section */}
            {dynamicDomainProps.length > 0 && sharedCNAME && (
                <Card>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                        <div style={{ flex: 1 }}>
                            <Title level={5} style={{ margin: 0, marginBottom: 4 }}>Additional Domains</Title>
                            <div style={{ fontSize: 14, color: 'rgba(0, 0, 0, 0.45)' }}>
                                Add multiple custom domains that share the same infrastructure and CNAME target.
                            </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {hasUnsavedChanges && (
                                <Button
                                    type="primary"
                                    onClick={deployDynamicChanges}
                                    loading={deploying}
                                    icon={deploying ? <Loader2 size={14} className="animate-spin" /> : undefined}
                                >
                                    Apply Changes
                                </Button>
                            )}
                            <Button
                                type="primary"
                                ghost
                                icon={<Plus size={14} />}
                                onClick={() => handleOpenDynamicWizard(dynamicDomainProps[0][0])}
                            >
                                Add Domain
                            </Button>
                        </div>
                    </div>
                    <Divider />
                    {dynamicDomains.length > 0 ? (
                        <div style={{ margin: '-8px 0' }}>
                            {dynamicDomains.map((domain, index) => (
                                <DomainRow
                                    key={domain.id}
                                    item={domain}
                                    onRemove={() => handleRemoveDynamicDomain(domain.domain)}
                                    isLast={index === dynamicDomains.length - 1}
                                />
                            ))}
                        </div>
                    ) : (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={
                                <Text type="secondary">
                                    No additional domains configured. Click "Add Domain" to get started.
                                </Text>
                            }
                        />
                    )}
                </Card>
            )}

            {/* Domain Wizard Modal */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingBottom: 4 }}>
                        <Globe size={20} style={{ color: 'var(--primary-color, #1890ff)' }} />
                        <Typography.Title level={4} style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
                            Configure Custom Domain
                        </Typography.Title>
                    </div>
                }
                open={isWizardOpen}
                onCancel={() => {
                    setIsWizardOpen(false);
                    setWizardContext(null);
                }}
                width={900}
                destroyOnClose
                styles={{
                    body: {
                        paddingTop: 8,
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }
                }}
                footer={wizardFooter}
            >
                {wizardContext && (
                    <DomainWizard
                        envId={envId!}
                        resourceName={component.name}
                        domainCNAME={wizardContext.domainItem?.cname || sharedCNAME!}
                        awsAccount={awsAccount}
                        initialDomain={wizardContext.domainItem?.domain}
                        onComplete={handleWizardComplete}
                        onCancel={() => {
                            setIsWizardOpen(false);
                            setWizardContext(null);
                            setWizardFooter(null);
                        }}
                        submitButtonText={wizardContext.type === 'static' ? "Deploy Now" : "Add Domain"}
                        onFooterChange={setWizardFooter}
                    />
                )}
            </Modal>
        </Space>
    );
};

export default ResourceSettingsCustomDomain;
